import React from 'react';
import { Building2, Wallet, CreditCard, Smartphone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { usePaymentAccountInfo } from '@/hooks/usePaymentAccountInfo';
import type { Database } from '@/integrations/supabase/types';

type PaymentAccount = Database['public']['Tables']['payment_accounts']['Row'];

interface PaymentAccountDisplayProps {
  accountId: string | number | null | undefined;
  className?: string;
  showBalance?: boolean;
  compact?: boolean;
}

const AccountIcon = ({ type }: { type: string | null }) => {
  const normalizedType = type?.toLowerCase();

  switch (normalizedType) {
    case 'checking':
    case 'corrente':
      return <Building2 className="h-3 w-3" />;
    case 'savings':
    case 'poupanca':
      return <Wallet className="h-3 w-3" />;
    case 'credit_card':
    case 'cartao_credito':
      return <CreditCard className="h-3 w-3" />;
    case 'digital_wallet':
    case 'pix':
      return <Smartphone className="h-3 w-3" />;
    default:
      return <Wallet className="h-3 w-3" />;
  }
};

const AccountTypeLabel = ({ type }: { type: string | null }) => {
  const normalizedType = type?.toLowerCase();

  switch (normalizedType) {
    case 'checking':
    case 'corrente':
      return 'C.Corrente';
    case 'savings':
    case 'poupanca':
      return 'Poupança';
    case 'credit_card':
    case 'cartao_credito':
      return 'Cartão';
    case 'digital_wallet':
    case 'pix':
      return 'Digital';
    default:
      return type ? type.substring(0, 6) : 'Outro';
  }
};

const formatBalance = (balance: number | null) => {
  if (balance === null) return '';

  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Math.abs(balance));

  return balance < 0 ? `-${formatted}` : formatted;
};

const getBalanceColor = (balance: number | null, accountType: string | null) => {
  if (balance === null) return 'text-muted-foreground';

  const isCredit = accountType?.toLowerCase().includes('credit');

  if (isCredit) {
    return balance < 0 ? 'text-red-600' : 'text-green-600';
  }

  return balance < 0 ? 'text-red-600' : 'text-foreground';
};

export const PaymentAccountDisplay: React.FC<PaymentAccountDisplayProps> = ({
  accountId,
  className = '',
  showBalance = false,
  compact = false
}) => {
  const { getAccountInfo } = usePaymentAccountInfo();
  const account = getAccountInfo(accountId);

  if (!account) {
    return (
      <div className={cn("text-muted-foreground text-xs", className)}>
        <span className="flex items-center gap-1">
          <Wallet className="h-3 w-3" />
          Sem conta
        </span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <AccountIcon type={account.type} />
        <span className="text-xs text-foreground truncate max-w-20">
          {account.name}
        </span>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <AccountIcon type={account.type} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-foreground truncate">
            {account.name}
          </span>
          <Badge variant="outline" className="text-xs px-1 py-0 h-4">
            <AccountTypeLabel type={account.type} />
          </Badge>
        </div>
        {showBalance && (
          <p className={cn(
            "text-xs",
            getBalanceColor(account.balance, account.type)
          )}>
            {formatBalance(account.balance)}
          </p>
        )}
      </div>
    </div>
  );
};

// Para compatibilidade com cn import que pode não estar disponível
function cn(...classes: (string | undefined | null | boolean)[]): string {
  return classes.filter(Boolean).join(' ');
}

export default PaymentAccountDisplay;